import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Button from '@atlaskit/button'
import Spinner from '@atlaskit/spinner'
import Tooltip from '@atlaskit/tooltip'
import StatusProgressBar from '../reports/charts/StatusProgressBar'
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add'
import CrossIcon from '@atlaskit/icon/glyph/cross'
import DocumentsIcon from '@atlaskit/icon/glyph/documents'
import { useNotification } from '../../contexts/NotificationContext'
import { useLicense } from '../../contexts/LicenseContext'
import LicenseRequiredPage from '../common/LicenseRequiredPage'
import { getJiraContext, isJiraConnect, resizeIframe, openAddonModuleInNewTab } from '../../utils/jiraContext'
import { setActiveProjectId, DEFAULT_JIRA_PROJECT_ID } from '../../constants/config'
import { fetchLinkedIssuesForScenario } from '../../api/issueLinkApi'
import {
  fetchLinkedScenariosAsync,
  unlinkScenarioAsync,
  setCurrentIssueKey,
  selectLinkedScenarios,
  selectIssueLinkLoading,
  selectUnlinkLoading,
  selectCurrentIssueKey
} from '../../store/slices/issueLinkSlice'

/**
 * ISSUE PANEL
 *
 * Standalone panel component rendered on the Jira issue page via jiraIssueContents.
 * NOT wrapped in AppLayout — runs in its own Jira iframe without sidebar/navigation.
 *
 * Shows test scenarios linked to the current Jira issue with:
 * - Scenario key, name, release, folder, run status counts
 * - "Link" button to open selection modal
 * - "Unlink" action per scenario row
 */
function IssuePanel() {
  const dispatch = useDispatch()
  const { showError, showSuccess } = useNotification()
  const { isLicenseValid, licenseMessage } = useLicense()

  // Redux state
  const currentIssueKey = useSelector(selectCurrentIssueKey)
  const linkedScenarios = useSelector(selectLinkedScenarios)
  const loading = useSelector(selectIssueLinkLoading)
  const unlinkLoadingId = useSelector(selectUnlinkLoading)

  // Local state
  const [jiraProjectId, setJiraProjectId] = useState(null)

  // Set of already-linked scenario IDs for the modal
  const alreadyLinkedIds = useMemo(() => {
    return new Set(linkedScenarios.map(s => s.id))
  }, [linkedScenarios])

  /**
   * INITIALIZE PANEL
   * Gets Jira issue key and project ID from AP context on mount.
   */
  useEffect(() => {
    const initPanel = async () => {
      try {
        if (isJiraConnect()) {
          const context = await getJiraContext()
          const issueKey = context?.jira?.issue?.key
          const projectId = context?.jira?.project?.id

          if (projectId) {
            setActiveProjectId(projectId)
            setJiraProjectId(projectId)
          }

          if (issueKey) {
            dispatch(setCurrentIssueKey(issueKey))
            dispatch(fetchLinkedScenariosAsync(issueKey))
          }
        } else {
          // Dev mode fallback — use hardcoded values for testing
          const devIssueKey = 'DEMO-1'
          dispatch(setCurrentIssueKey(devIssueKey))
          dispatch(fetchLinkedScenariosAsync(devIssueKey))
          setJiraProjectId(DEFAULT_JIRA_PROJECT_ID)
        }
      } catch (error) {
        console.error('Failed to initialize issue panel:', error)
      }
    }
    initPanel()
  }, [dispatch])

  /**
   * RESIZE IFRAME
   * Adjusts iframe height when linked scenarios list changes.
   */
  useEffect(() => {
    resizeIframe()
  }, [linkedScenarios])

  /**
   * OVERRIDE BODY BACKGROUND
   * The global index.css sets body to var(--ds-surface-sunken) (grey).
   * For the issue panel iframe, override to var(--ds-surface) so it
   * matches the Jira issue page background (white in light, dark in dark theme).
   */
  useEffect(() => {
    const prevBg = document.body.style.backgroundColor
    document.body.style.backgroundColor = 'var(--ds-surface, #FFFFFF)'
    return () => {
      document.body.style.backgroundColor = prevBg
    }
  }, [])

  /**
   * HANDLE OPEN LINK DIALOG
   * Opens the link scenarios dialog using Jira's AP.dialog API.
   * The dialog renders as a full-screen overlay on top of Jira (outside the iframe).
   * On close, refreshes the linked scenarios list if linking occurred.
   */
  const handleOpenLinkDialog = () => {
    if (isJiraConnect() && window.AP?.dialog?.create) {
      window.AP.dialog.create({
        key: 'link-scenarios-dialog',
        chrome: false,
        customData: {
          jiraIssueKey: currentIssueKey,
          jiraProjectId: jiraProjectId,
          alreadyLinkedIds: Array.from(alreadyLinkedIds)
        }
      }).on('close', (data) => {
        // Refresh linked scenarios if user linked something
        if (data?.linked && currentIssueKey) {
          dispatch(fetchLinkedScenariosAsync(currentIssueKey))
        }
      })
    } else {
      console.warn('AP.dialog not available — cannot open link dialog in dev mode')
    }
  }

  /**
   * HANDLE UNLINK
   * Removes a scenario link from the current issue.
   * The linkId comes from the linked scenario's link record — we need to find it.
   * Since the API response for GET /jira-issues/{key}/testscenarios doesn't include
   * the link ID directly, we use the scenario's id as testScenarioId and need to
   * find the link. We'll use a two-step approach: fetch links for the scenario,
   * find the matching one, then delete it.
   *
   * UPDATE: Since the GET endpoint returns scenario details (not link records),
   * we need to call GET /testscenarios/{id}/jira-issues to find the linkId
   * for the current issue, then call DELETE. To simplify, we'll store the
   * linkId when available, or fetch it on demand.
   */
  const handleUnlink = async (scenario) => {
    try {
      // We need the link ID. The scenario object from GET /jira-issues/{key}/testscenarios
      // includes scenario details but may not include the linkId directly.
      // We'll fetch the links for this scenario to find the correct linkId.
      const links = await fetchLinkedIssuesForScenario(scenario.id)
      const matchingLink = links.find(l => l.jiraIssueKey === currentIssueKey)

      if (!matchingLink) {
        showError('Unlink Failed', 'Could not find the link record. Try refreshing.')
        return
      }

      await dispatch(unlinkScenarioAsync({
        testScenarioId: scenario.id,
        linkId: matchingLink.id,
        jiraIssueKey: currentIssueKey
      })).unwrap()

      showSuccess('Unlinked', `"${scenario.scenarioName}" has been unlinked from this issue.`)
    } catch (error) {
      showError('Unlink Failed', error?.message || 'Failed to unlink scenario.')
    }
  }

  /**
   * HANDLE SCENARIO CLICK
   * Opens the scenario detail page in a new browser tab so the Jira issue
   * stays open in the original tab. Builds an absolute Connect URL with the
   * route as a `#!` hash fragment — RouteSynchronizer reads it via
   * AP.history.getState() on load and navigates the new tab to the right page.
   *
   * The `?project=` hint forces AppLayout to select the scenario's project
   * on boot. Without it, the new tab inherits the parent Jira issue's
   * project context (wrong project in the sidebar when the issue and
   * scenario belong to different projects).
   *
   * scenarioKey is `{PROJECT_KEY}-{sequence}` (e.g. "SP-123"), so the prefix
   * before the first '-' is the project key. Jira project keys are
   * uppercase letters only, so this split is unambiguous.
   */
  const handleScenarioClick = (scenario) => {
    const projectKey = scenario.scenarioKey?.split('-')[0]
    const baseRoute = scenario.releaseId
      ? `/releases/${scenario.releaseId}/scenario/${scenario.id}`
      : `/scenario/${scenario.id}`
    const targetRoute = projectKey
      ? `${baseRoute}?project=${projectKey}`
      : baseRoute

    openAddonModuleInNewTab('test-management-page', targetRoute)
  }

  /**
   * AGGREGATE STATUS COUNTS ACROSS RUNS
   * Sums per-run statusCounts into a single map for the scenario.
   * Mirrors aggregateScenarioStatusCounts in dataTransformer.js, but operates
   * on the pre-aggregated `runs[].statusCounts` returned by
   * GET /jira-issues/{key}/testscenarios (which does not include test cases).
   *
   * The endpoint only counts actually-executed test cases (no "Not Run"
   * filler), so a freshly created run has statusCounts = {}. Aggregating
   * across all runs ensures the bar reflects the scenario's full execution
   * history instead of going blank when the latest run is empty.
   */
  const aggregateRunsStatusCounts = (runs) => {
    const counts = {}
    if (!runs) return counts
    for (const run of runs) {
      if (!run.statusCounts) continue
      for (const [status, count] of Object.entries(run.statusCounts)) {
        counts[status] = (counts[status] || 0) + count
      }
    }
    return counts
  }


  // License validation gate - show compact license page in issue panel
  if (!isLicenseValid) {
    return <LicenseRequiredPage message={licenseMessage} compact />
  }

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      padding: '12px 0',
      backgroundColor: 'var(--ds-surface, #FFFFFF)'
    }}>
      {/* Header — title comes from Jira panel chrome, only show the Link button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginBottom: '12px',
        padding: '0 4px'
      }}>
        <Button
          appearance="primary"
          spacing="compact"
          iconBefore={<EditorAddIcon label="Link" size="small" />}
          onClick={handleOpenLinkDialog}
          isDisabled={!currentIssueKey}
        >
          Link
        </Button>
      </div>

      {/* Content */}
      {loading ? (
        // Loading state
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '24px'
        }}>
          <Spinner size="medium" />
        </div>
      ) : linkedScenarios.length > 0 ? (
        // Linked scenarios list
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px'
        }}>
          {linkedScenarios.map(scenario => {
            const aggregatedCounts = aggregateRunsStatusCounts(scenario.runs)
            const totalExecuted = Object.values(aggregatedCounts).reduce((s, c) => s + c, 0)
            const isUnlinking = unlinkLoadingId !== null

            return (
              <div
                key={scenario.id}
                onClick={() => handleScenarioClick(scenario)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid var(--ds-border, #DFE1E6)',
                  backgroundColor: 'var(--ds-surface, #FFFFFF)',
                  gap: '10px',
                  cursor: 'pointer',
                  transition: 'background-color 0.1s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--ds-background-neutral-subtle-hovered, #F4F5F7)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--ds-surface, #FFFFFF)'
                }}
              >
                {/* Scenario key */}
                <span style={{
                  fontSize: '12px',
                  color: 'var(--ds-text-subtlest, #6B778C)',
                  fontFamily: 'monospace',
                  whiteSpace: 'nowrap'
                }}>
                  {scenario.scenarioKey}
                </span>

                {/* Scenario name */}
                <span style={{
                  flex: 1,
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'var(--ds-text, #172B4D)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {scenario.scenarioName}
                </span>

                {/* Release name */}
                {scenario.releaseName && (
                  <Tooltip content={`Release: ${scenario.releaseName}`}>
                    <span style={{
                      fontSize: '11px',
                      color: 'var(--ds-text-subtlest, #6B778C)',
                      backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      whiteSpace: 'nowrap'
                    }}>
                      {scenario.releaseName}
                    </span>
                  </Tooltip>
                )}

                {/* Aggregated execution status across all runs.
                    Falls back to a test-case-count chip when no run has any executions yet. */}
                {totalExecuted > 0 ? (
                  <StatusProgressBar
                    statusDistribution={aggregatedCounts}
                    height={6}
                    width="80px"
                    showTooltip={true}
                  />
                ) : scenario.testCaseCount > 0 ? (
                  <Tooltip content={`${scenario.testCaseCount} test case(s)`}>
                    <span style={{
                      fontSize: '11px',
                      color: 'var(--ds-text-subtlest, #6B778C)',
                      backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
                      padding: '2px 6px',
                      borderRadius: '3px'
                    }}>
                      {scenario.testCaseCount} {scenario.testCaseCount === 1 ? 'case' : 'cases'}
                    </span>
                  </Tooltip>
                ) : null}

                {/* Unlink button */}
                <Tooltip content="Unlink scenario">
                  <Button
                    appearance="subtle"
                    spacing="compact"
                    iconBefore={<CrossIcon label="Unlink" size="small" />}
                    onClick={(e) => { e.stopPropagation(); handleUnlink(scenario) }}
                    isDisabled={isUnlinking}
                  />
                </Tooltip>
              </div>
            )
          })}
        </div>
      ) : (
        // Empty state
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          textAlign: 'center'
        }}>
          <DocumentsIcon
            label="No scenarios"
            size="large"
            primaryColor="var(--ds-text-subtlest, #6B778C)"
          />
          <p style={{
            fontSize: '13px',
            color: 'var(--ds-text-subtlest, #6B778C)',
            margin: '12px 0 0'
          }}>
            No test scenarios linked to this issue.
          </p>
          <p style={{
            fontSize: '12px',
            color: 'var(--ds-text-subtlest, #6B778C)',
            margin: '4px 0 0'
          }}>
            Click <strong>Link</strong> to associate test scenarios.
          </p>
        </div>
      )}

    </div>
  )
}

export { IssuePanel }
